// @flow

import { Component } from '../jsti/core';
import EventNames from '../constants/EventNames';
import Input from '../classes/Input';
import Error from '../classes/Validation/Error';
import ValidationChecks from '../constants/Validation/ValidationChecks';
import ClassNames from '../constants/ClassNames';
import Notifications from '../constants/Notifications';
import { da, de, en, fr, nl } from 'intl-tel-input/i18n';
import intlTelInput from 'intl-tel-input';

export default class Validator extends Component {
  #blurHandler: function;
  inputs: Array<Input> = [];
  error: Error;
  type: string;
  invalidOnly: boolean = false;
  isBlocked: false;
  iti;
  hiddenPhoneNumber: HTMLInputElement;

  /**
   * Get the inputs that have been touched
   */
  get touchedInputs(): Array<Input> {
    return this.inputs.filter((input: Input) => input.hasBeenTouched());
  }

  /**
   * Initialize the component
   *
   * @param pEl
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);
    this.setUp();

    this.#blurHandler = this.onBlur.bind(this);

    if (!this.el.dataset.notificationsOnly) {
      this.inputs.forEach((input: Input) => input.element.addEventListener(EventNames.BLUR, this.#blurHandler));
    }

    this.addListener(Notifications.VALIDATE, this.onNotification);

    this.handlePreFill();
  }

  /**
   * Validation and set up properties
   */
  setUp(): void {
    const inputs: Array<HTMLInputElement> = [...this.el.querySelectorAll('input')];
    if (!inputs.length) {
      throw new Error('At least one input element is required!');
    }

    this.inputs = inputs.map((element: HTMLInputElement) => new Input(element));

    this.type = this.el.dataset.type;
    if (this.type === 'tel') {
      this.setInTelInput(inputs[0]);
    }

    this.invalidOnly = !!this.el.dataset.invalidOnly;
  }

  /**
   * Executes when the input is blurred
   */
  async onBlur(event: Event): void {
    const input: ?Input = Input.getInputByElement(this.inputs, event.target);
    if (!input || !input.valueHasChanged()) return;

    input.rememberValue();

    if (this.touchedInputs.length !== this.inputs.length) return;

    await this.validate();
  }

  /**
   * Executes when a notification is received
   */
  async onNotification(): void {
    await this.validate();
  }

  /**
   * Executes when the field is prefilled
   */
  async handlePreFill(): void {
    if (this.touchedInputs.length !== this.inputs.length || this.type === 'tel') return;

    await this.validate();
  }

  /**
   * Validate the input field
   * Returns if the validator has passed
   */
  async validate(): Promise<boolean> {
    this.reset();
    const invalidInputs: Array<Input> = this.invalidInputs();

    if (invalidInputs.length) {
      this.onInvalid(invalidInputs);
      return false;
    }

    if (!(await this.isValid())) {
      this.onInvalid(this.inputs);
      return false;
    }

    if (this.invalidOnly) return true;

    this.onValid();
    return true;
  }

  /**
   * Get the inputs that are invalid
   */
  invalidInputs(): Array<Input> {
    return this.inputs.filter((input: Input) => {
      if (input.element.dataset.length && (input.value.length > input.element.dataset.length)) return true;
      return !input.isValid();
    });
  }

  /**
   * Extra security check to check if the combination of all inputs is valid
   */
  async isValid(): Promise<boolean> {
    if (this.type === 'date') {
      return this.validateDate();
    }

    if (this.type === 'postal-code') {
      return this.validatePostalCode();
    }

    if (this.type === 'password') {
      return this.validatePassword();
    }

    if (this.type === 'email') {
      return this.validateEmail();
    }

    if (this.type === 'tel') {
      return this.validateTel();
    }

    return true;
  }

  /**
   * Executes when the input field is valid
   */
  onValid(): void {
    this.el.classList.add(ClassNames.VALID);
  }

  /**
   * Executes when the input field is invalid
   */
  onInvalid(invalidInputs: Array<Input>): void {
    if (!invalidInputs.length) return;

    this.error = new Error(this.message(invalidInputs[0]));
    this.error.add(this.el);
  }

  /**
   * Reset the validator element
   */
  reset(): void {
    this.el.classList.remove(ClassNames.VALID);
    this.isBlocked = false;

    if (this.error) {
      this.error.remove(this.el);
      this.error = undefined;
    }
  }

  /**
   * Get the correct error message
   */
  message(input: Input): string {
    if (!this.type) {
      return window.store.getTranslation(`validation.${this.reason(input)}`);
    }

    return window.store.getTranslation(`validation.${this.type}.${this.reason(input)}`);
  }

  /**
   * Get the reason why a validation fails
   */
  reason(input: Input): string {
    if (this.type === 'date') return ValidationChecks.DOES_NOT_MATCH_PATTERN;

    if (!input.hasValue()) return ValidationChecks.IS_REQUIRED;

    if (this.isBlocked) return ValidationChecks.BLOCKED;

    if (input.element.dataset.length && (input.value.length > input.element.dataset.length)) return ValidationChecks.LENGTH;

    return ValidationChecks.DOES_NOT_MATCH_PATTERN;
  }

  /**
   * Validate a date
   */
  validateDate(): boolean {
    const YEAR_LENGTH = 4;

    let inputs: Array<Input> = this.inputs;
    if (inputs[0].value.length !== YEAR_LENGTH) {
      inputs = inputs.reverse();
    }

    const originalDate: string = inputs.map((input: Input) => parseInt(input.value, 10)).join('/');

    const dateObject: Date = new Date(originalDate);
    const generatedDate: string = `${dateObject.getFullYear()}/${dateObject.getMonth() + 1}/${dateObject.getDate()}`;

    return originalDate === generatedDate;
  }

  /**
   *
   */
  validatePostalCode(): boolean {
    const blockedRegex = this.el.dataset.blocked;
    if (!blockedRegex) return true;
    let blockedList = [];
    try {
      blockedList = JSON.parse(blockedRegex).filter((blocked: string) => {
        return new RegExp(blocked, 'i').test(this.inputs[0].value);
      });
    } catch {
      blockedList = [];
    }
    this.isBlocked = Boolean(blockedList.length);
    return !(blockedList.length);
  }

  /**
   *
   */
  validatePassword(): boolean {
    const input = this.inputs[0];
    if (!input.element.required && !input.value.length) {
      return true;
    }

    const minimumPasswordLength = input.element.minLength || 8;
    return input.value.length >= minimumPasswordLength;
  }

  validateEmail(): boolean {
    const input = this.inputs[0];
    if (!input.element.required && !input.value.length) {
      return true;
    }

    return new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/).test(this.inputs[0].value);
  }

  validateTel(): boolean {
    if (!this.iti) return true;
    this.hiddenPhoneNumber.value = this.iti.getNumber();
    return true;
    // if (!input.element.required && !input.value.length) {
    //   return true;
    // }
    // return this.iti.isValidNumber();
  }

  setInTelInput(input: HTMLInputElement): void {
    const translations = { nl, fr, en, de, da };
    const i18n = translations[window.locale.split('-')[0]] || en;
    const phoneNumber = input.value;

    this.iti = intlTelInput(input, {
      i18n: i18n,
      initialCountry: window.locale.split('-')[1],
      nationalMode: false,
      strictMode: true,
      useFullscreenPopup: false,
      validationNumberTypes: null,
      loadUtils: () => import('intl-tel-input/utils')
    });
    this.iti.promise.then(async () => {
      if (phoneNumber.length > 1) {
        this.iti.setNumber(phoneNumber);
        await this.validate();
      }
    });

    input.addEventListener('countrychange', this.validate.bind(this));
    this.hiddenPhoneNumber = document.createElement('input');
    this.hiddenPhoneNumber.setAttribute('type', 'hidden');
    this.hiddenPhoneNumber.setAttribute('name', 'full-phone');
    input.parentElement.appendChild(this.hiddenPhoneNumber);
  }

  /**
   * Destroy all event listeners
   */
  destroy() {
    this.inputs.forEach((input: Input) => input.element.removeEventListener(EventNames.BLUR, this.#blurHandler));
    super.destroy();
  }
}
